import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { AppConfiguration } from '@config/app-configuration';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { Location } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class AppTitleService {
  constructor(
    private router: Router,
    private title: Title,
    private appConfig: AppConfiguration,
    private translate: TranslateService,
    private location: Location
  ) {}

  init(): void {
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        const pageTitle = this.resolveCurrentRouteTitle(this.router.routerState.root);
        const formatted = this.formatTitle('smiONE Card', pageTitle);
        this.title.setTitle(formatted);
      });

    this.location.onUrlChange((url: string) => {
      const mapped = this.resolveTitleFromUrl(url);
      if (mapped) {
        const formatted = this.formatTitle('smiONE Card', mapped);
        this.title.setTitle(formatted);
      }
    });

    const initialUrl = (window && window.location && window.location.pathname) || '';
    const initialMapped = this.resolveTitleFromUrl(initialUrl);
    if (initialMapped) {
      const formatted = this.formatTitle('smiONE Card', initialMapped);
      this.title.setTitle(formatted);
    }
  }

  private resolveCurrentRouteTitle(route: ActivatedRoute): string | null {
    let current = route;
    while (current.firstChild) {
      current = current.firstChild;
    }

    const dataTitle = (current.snapshot && current.snapshot.data && current.snapshot.data['title']) || null;
    const configTitle = (current.routeConfig && (current.routeConfig as any).title) || null;
    const rawTitle: string | null = dataTitle || configTitle || null;

    if (!rawTitle) return null;
    const translated = this.translate.instant(rawTitle);
    return translated && translated !== rawTitle ? translated : rawTitle;
  }

  private formatTitle(appName: string, pageTitle: string | null): string {
    if (!pageTitle) return appName;
    return `${pageTitle} | ${appName}`;
  }

  private resolveTitleFromUrl(url: string): string | null {
    if (!url) return null;
    const path = (url.startsWith('/') ? url : `/${url}`).toLowerCase();
    const TITLE_MAP: Record<string, string> = {
      '/smione/card/recentactivity': 'Recent Activity',
      '/smione/card/statements': 'Statements',
      '/smione/card/activityanalysis': 'Activity Analysis',
      '/smione/card/feeschedule': 'Fee Schedule',

      '/smione/manage/activate': 'Activate Card',
      '/smione/manage/managecards': 'Card Settings',
      '/smione/manage/directdeposit': 'Direct Deposit',
      '/smione/manage/depositcash': 'Deposit Cash',
      '/smione/manage/transferfunds': 'Transfer Funds',
      '/smione/manage/billpayments': 'Bill Payments',

      '/smione/settings/personalinfo': 'Personal Info',
      '/smione/settings/notifications': ' Notification Settings',
      '/smione/settings/securityquestions': 'Security Questions',
      '/smione/settings/changepassword': 'Change Password',
      '/smione/settings/cardnicknames': 'Card Nicknames',
    };
    const keyOrText = TITLE_MAP[path] || null;
    if (!keyOrText) return null;
    const translated = this.translate.instant(keyOrText);
    return translated && translated !== keyOrText ? translated : keyOrText;
  }
}
